import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getAdditionalResources } from 'redux/selectors/help-options';
import { AdditionalResourceStatus } from 'redux/schemas/models/help-options';
import { MAX_NUMBER_OF_FILES, ERROR_DISPLAY_DELAY } from '../constants';
import { consolidateErrors } from '../utils/error-messages';
import { teachingTeam } from '../../styles';
import UploadArea from './upload-area';
import ResourceListItem from './resource-list-item';

interface AdditionalResourcesSectionProps {
  disabled?: boolean;
}

const AdditionalResourcesSection = ({ disabled = false }: AdditionalResourcesSectionProps) => {
  const dispatch = useAppDispatch();
  const resources = useSelector(getAdditionalResources);
  const [errorQueue, setErrorQueue] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const isUploading = resources.some((r) => r.status === AdditionalResourceStatus.UPLOADING);
  const isDisabled = disabled || resources.length >= MAX_NUMBER_OF_FILES;

  useEffect(() => {
    if (!isUploading && errorQueue.length > 0) {
      const timer = setTimeout(() => {
        const { message } = consolidateErrors(errorQueue);

        dispatch(
          addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message,
          }),
        );

        setErrorQueue([]);
      }, ERROR_DISPLAY_DELAY);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isUploading, errorQueue, dispatch]);

  const handleValidationErrors = (errors: string[]) => {
    setErrorQueue((prev) => [...prev, ...errors]);
  };

  const handleUploadError = (errorMessage: string) => {
    setErrorQueue((prev) => [...prev, errorMessage]);
  };

  return (
    <div
      className='mt-4'
      css={teachingTeam}
      ref={containerRef}
    >
      <p className='font-weight-bold mb-2'>
        {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.TITLE()}
      </p>
      <p className='text-body mb-3'>
        {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.DESCRIPTION()}
      </p>

      {!isUploading && (
        <UploadArea
          disabled={isDisabled}
          onValidationErrors={handleValidationErrors}
        />
      )}

      {resources.length > 0 && (
        <div className='mt-3'>
          <div className='uploaded-files-list'>
            {resources.map((resource) => (
              <ResourceListItem
                key={resource.uniqueId}
                resource={resource}
                onUploadError={handleUploadError}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalResourcesSection;

